import { DecimalPipe, DatePipe } from "@angular/common";
import { Component, ChangeDetectionStrategy, OnInit, OnDestroy, Output, EventEmitter, Input, ChangeDetectorRef, ViewChild } from "@angular/core";
import { AppService } from "@irb/app.service";
import { CalltoactionService } from "@irb/irb-common/components/calltoaction/calltoaction.service";
import { IEvent, IrbAppEvents, IrbClientsEvents, IrbModelsEvents } from "@irb/irb-common/events";
import { ICoveredCall, ICoveredCallAllocation, ISecurityDropDown, ISecurityDropDownItem } from "@irb/irb-editors/rebalance/irb-rebalance.model";
import { RebalanceService } from "@irb/irb-editors/rebalance/rebalance.service";
import { OptionsFormatterService } from "../../options-formatter.service";
import { RollCoveredCallGridLayoutService } from "./roll-coveredcall-settings.grid-layout.service";
import { IrbOptionChainsService } from "@irb/irb-option-chains/irb-option-chains.service";
import { Subscription } from "rxjs";
import { UtilityService } from "@irb/irb-common/services/utility.service";
import { DropdownSearchComponent } from "@irb/irb-common/components/dropdown-search/dropdown-search.component";
import { IDropdownItem } from "@irb/irb-common/models/dropdown.model";

@Component({ standalone: false,
    selector: "roll-coveredcall-settings",
    templateUrl: "./roll-coveredcall-settings.component.html",
    styleUrls: [ 
        "./roll-coveredcall-settings.component.scss"
    ],
    changeDetection: ChangeDetectionStrategy.Eager
})
export class RollCoveredcallSettingsComponent implements OnInit, OnDestroy {
    @ViewChild("rollCoveredCallDropdown") rollCoveredCallDropdown: DropdownSearchComponent;

    @Output() change = new EventEmitter<any>();

    _data: ICoveredCall;
    _isReadOnly: boolean;

    _defaultPercentOfCoverCallToRoll: number;
    _isCashTarget: boolean = true;
    _isShortPosition: boolean = true;
    _positionType: string = 'SHORT'
    _securities: IDropdownItem[];
    _underlyingSymbol: ISecurityDropDownItem;

    _grid: any;
    _gridOptions: any;

    editedRowIndex: number;
    isOptnSymbToRollModified: boolean;

    private optionChainSubscription: Subscription;
    private currentAllocation: ICoveredCallAllocation;
    isUiTest = false;
    frameworkComponents: any;
    
    constructor(private cdr: ChangeDetectorRef,
        private gls: RollCoveredCallGridLayoutService,
        private ctas: CalltoactionService,
        public decimalPipe: DecimalPipe,
        private datePipe: DatePipe,
        private as: AppService,
        private readonly rebalanceService: RebalanceService,
        public optionFormatter: OptionsFormatterService,
        private irbOptionChainsService: IrbOptionChainsService,
        private utilService: UtilityService) {

        this.defaultPercentOfCoverCallToRoll = 100;

        this.gridOptions = {
            columnDefs: this.gls.getColumnDefs(this),            
            floatingFilter: false,
            context: { componentParent: this },
            isExternalFilterPresent: this.isExternalFilterPresent.bind(this),
            doesExternalFilterPass: this.doesExternalFilterPass.bind(this)
        };
        
        this.frameworkComponents = this.gls.getFrameworkComponents();

        this.optionChainSubscription = this.irbOptionChainsService.optionChainSymbolSelections$.subscribe((optionData) => {
            if (this.editedRowIndex !== null && this.editedRowIndex !== undefined && 
                this.isOptnSymbToRollModified !== null && this.isOptnSymbToRollModified !== undefined) {
                    this.optnSymbToRollModified(optionData[0]);
            } else if (this.editedRowIndex !== null && this.editedRowIndex !== undefined) {
                this.optionChainEdited(optionData[0]);
            } else {
                this.optionChainSelected(optionData);
            }
        });
    }

    optionChainSelected(optionChainData: Array<string>) {
        this.populateAllocation("OPTION_CHAIN", optionChainData[0][1][0], optionChainData[0][0]);
        this.loadGrid();
        this.change.emit(true);
    }

    optionChainEdited(editedOptonChain: any) {
        this.data.allocations[this.editedRowIndex].optionSelectionMode = "OPTION_CHAIN";
        this.data.allocations[this.editedRowIndex].optionSymbol = editedOptonChain[1][0];
        this.data.allocations[this.editedRowIndex].daysToExpiration = null;
        this.editedRowIndex = null;
        this.loadGrid();
        this.change.emit(true);
    }

    optnSymbToRollModified(editedOptonChain: any) {
        this.data.allocations[this.editedRowIndex].optionSymbolToRollTo = editedOptonChain[1][0];
        this.editedRowIndex = null;
        this.isOptnSymbToRollModified = null;
        this.loadGrid();
        this.change.emit(true);
    }

    getExistingAllocMap() {
        let existingAllocMap: Map<string, ICoveredCallAllocation> = new Map()
        this.data.allocations.filter(allocation => allocation.isUIdeleted === false).forEach((allocation: ICoveredCallAllocation) => {
            existingAllocMap.set(allocation.underlyingSymbol, allocation);
        });
        return existingAllocMap;
    }

    onGridReady(params: any) {
        this.grid = params;
        this.grid.api.onFilterChanged();
        this.load();
    }

    loadGrid() {
        this.grid.api.setGridOption('rowData', this.data.allocations);
        // setTimeout(() => {
        //     if (this.grid && this.grid.api) {
        //         this.grid.api.sizeColumnsToFit();
        //     }
        // });
    }

    ngOnInit(): void {
        this.as.getEvents().subscribe((s) => { this.processEvent(s); });
    }

    private processEvent(event: IEvent) {
        switch (event.type) {
            case IrbModelsEvents.SAVE_CREATE_NEW_SECURITY:                
                this.rebalanceService.getEquitySecuritiesDropDown().subscribe((data:  {items: ISecurityDropDownItem[]}) => {
                    this.securities = data.items.map(security => ({name: security.securitySymbol, value: security}));
                    this.securities = this.utilService.sortByKey(this.securities, 'securitySymbol', 'asc');
                    });
                this.addSecurityAllocationToGrid(event);
               
                break;
        }
    }

    addSecurityAllocationToGrid(event: IEvent){
        const newData = event.data;
        let isEquitySecurities = false;
        if (newData.securities && newData.securities.responseData && newData.securities.responseData.length !== 0) {
            newData.securities.responseData.forEach((item: any) => {
                if(item.securityType && item.securityType === "EQUITY"){
                    this.underlyingSymbol = item;
                    this.addUnderlyingSymbol();                   
                }else{
                    isEquitySecurities = true;
                }
                
            });
            if(isEquitySecurities){
                this.as.fireEvent(IrbAppEvents.SHOW_TOAST, {
                    severity: 'warn', 
                    summary: "New Security", 
                    detail: " Only Equity securities can be added to Covered Call Rebalances"
                  });
            }
        }
    }

    ngOnDestroy(): void {
        this.optionChainSubscription.unsubscribe();
    }

    load() {
        if (!this.isReadOnly) {
            this.rebalanceService.getEquitySecuritiesDropDown().subscribe((data: {items: ISecurityDropDownItem[]}) => {
                this.securities = data.items.map(security => ({name: security.securitySymbol, value: security}));
                this.securities = this.utilService.sortByKey(this.securities, 'securitySymbol', 'asc');
            });
            this.data.allocations = [];
        } else {
            this.populateIsCashTarget();
            this.populatePositionType();
        }
        this.loadGrid();
    }

    // Use string from search input to filter the security items, if no string, set all items
    populateSecuritiesDropdown(searchString: string) {
        if (searchString && searchString.length > 0) {
            const searchTerm = searchString.toUpperCase();

            this.rollCoveredCallDropdown.set(
                (this.securities || []).filter(
                    (security) =>
                        security.name &&
                        security.name.toUpperCase().includes(searchTerm)
                )
            );
        } else {
          this.rollCoveredCallDropdown.set(this.securities || []);
        }
    }

    // Set underlying symbol from selected item from dropdown
    onUnderlyingSymbolSelect(dropdownItem: IDropdownItem) {
        this.underlyingSymbol = dropdownItem.value;
    }
    
    // Clear underlying symbol and reset security items when input is cleared (by erasing the text)
    clearBuyToCoverSecurity() {
        this.underlyingSymbol = null;
        this.rollCoveredCallDropdown.set(this.securities || []);
    }

    populateIsCashTarget() {
        if (this.data.buyUsingCashTarget === true && this.data.buyUsingCashMinimum === false) {
            this.isCashTarget = true;
        } else {
            this.isCashTarget = false;
        }
    }

    applyPercentOfCoverCallToRoll() {
        if (this.data.allocations && this.data.allocations.length > 0) {
            this.data.allocations.forEach(allocation => {
                allocation.percentOfCoverCallToRoll = Number(this.defaultPercentOfCoverCallToRoll);
            });
            this.loadGrid();
            this.change.emit(true);
        }
        this.defaultPercentOfCoverCallToRoll = 100;
    }

    onCashMinTargetChange() {
        if (this.isCashTarget) {
            this.data.buyUsingCashTarget = true;
            this.data.buyUsingCashMinimum = false;
        } else {
            this.data.buyUsingCashTarget = false;
            this.data.buyUsingCashMinimum = true;
        }
        this.change.emit(true);
    }

    populatePositionType() {
        if (this.data.allocations) {
            for (let i = 0; i < this.data.allocations.length; i++) {
                this.positionType = this.data.allocations[i].positionType;
                break;
            }
        }
    }

    onPositionTypeChange() {
        if (this.isShortPosition) {
            this.positionType = 'SHORT';
        } else {
            this.positionType = 'LONG';
        }
        if (this.data.allocations) {
            this.data.allocations.forEach(allocation => {
                allocation.positionType = this.positionType;
            });
        }
    }

    addUnderlyingSymbol() {
        this.setBuyToCoverAllocation();
        this.underlyingSymbol = null;
        this.loadGrid();
        // Reset the dropdown when symbol is added to grid
        this.rollCoveredCallDropdown.clearInput();
        this.change.emit(true);
    }

    populateAllocation(optionSelectionMode: string, optionSymbol: string, underlyingSymb?: string) {
        let allocation: ICoveredCallAllocation = {
            underlyingSymbol: (optionSelectionMode === 'OPTION_CHAIN') ? underlyingSymb : this.underlyingSymbol.securitySymbol,
            //underlyingId: this.underlyingSymbol.securityId,
            underlyingTarget: "MODEL_PERC",
            optionDaysToExpiration: null,
            optionSymbol: optionSymbol,
            optionSymbolToRollTo: null,
            percentOfUnderlyingToCover: 100,
            percentOfUnderlyingToPurchase: null,
            isUIdeleted: false,
            percentOfUnderlyingToTrim: 100,
            percentOfOptionToBuyBack: 100,
            percentOfCoverCallToRoll: Number(this.defaultPercentOfCoverCallToRoll),
            allocationId: null,
            optionSecId: null,
            optionSelectionMode: optionSelectionMode,
            daysToExpiration: null,
            positionType: this.positionType
        };
        
        this.data.allocations.push(allocation);
    }

    setBuyToCoverAllocation() {
        let existingSymbolOSMMap: Map<any, any> = this.getAllExistingSymbolOSM();
        let availabeOSM: string[] = ['ALL_CALLS', 'ALL_PUTS'];
        if (existingSymbolOSMMap && existingSymbolOSMMap.size > 0) {
            if (existingSymbolOSMMap.has(this.underlyingSymbol.securitySymbol)) {
                let existingOSM: string[] = existingSymbolOSMMap.get(this.underlyingSymbol.securitySymbol);
                for (let i = 0; i < availabeOSM.length; i++) {
                    existingOSM.forEach(osm => {
                        if (osm === availabeOSM[i]) {
                            availabeOSM.splice(i, 1);
                        }
                    });
                };
            }
        }

        if (availabeOSM.length > 0) {
            this.populateAllocation(availabeOSM[0], null);
        } else {
            //If no option selection mode available then select from option chain
            this.as.fireEvent(IrbClientsEvents.SHOW_OPTION_CHAINS_DIALOG, {
                data: {
                    action: 'ROLL_COVER_CALL', 
                    underlyingSymbol: this.underlyingSymbol.securitySymbol, 
                    chainType: 'ALL',
                    isChainTypeDisable: false,
                    isTrayVisible: false
                }
            });
        }
    }

    getSelectedOptions() {
        let selectedOptions: { symbol: string; optionSymbol: string; }[] = [];
        this.data.allocations.filter(allocation => allocation.isUIdeleted === false).forEach((allocation: ICoveredCallAllocation) => {
            selectedOptions.push({symbol: allocation.underlyingSymbol, optionSymbol: allocation.optionSymbol});
        });
        return selectedOptions;
    }

    getAllExistingSymbolOSM() {
        //This method will return the existing underlying symbol and optionSelectionMode in the grid
        let existingSymbolOSMMap: Map<string, string[]> = new Map();
        if (this.data.allocations) {
            this.data.allocations.forEach(allocation => {
                if (allocation.optionSelectionMode && !allocation.isUIdeleted) { 
                    if (allocation.optionSelectionMode === 'ALL_CALLS' || allocation.optionSelectionMode === 'ALL_PUTS') {
                        if (existingSymbolOSMMap.has(allocation.underlyingSymbol)) {
                            existingSymbolOSMMap.get(allocation.underlyingSymbol).push(allocation.optionSelectionMode);
                        } else {
                            let optnModes: string[] = [allocation.optionSelectionMode];
                            existingSymbolOSMMap.set(allocation.underlyingSymbol, optnModes);
                        }
                    } else {
                        if (existingSymbolOSMMap.has(allocation.underlyingSymbol)) {
                            existingSymbolOSMMap.get(allocation.underlyingSymbol).push(allocation.optionSymbol);
                        } else {
                            let optnModes: string[] = [allocation.optionSymbol];
                            existingSymbolOSMMap.set(allocation.underlyingSymbol, optnModes);
                        }
                    }
                }
            });
        }
        return existingSymbolOSMMap;
    }

    getExistingSymbolOSM(underlyingSymbol: string) {
        //This method will return the existing underlying symbol's optionSelectionModes in the grid
        let optnModes: Array<string> = new Array();
        if (this.data.allocations) {
            this.data.allocations.forEach(allocation => {
                if (allocation.optionSelectionMode && !allocation.isUIdeleted && allocation.underlyingSymbol === underlyingSymbol) { 
                    if (allocation.optionSelectionMode === 'ALL_CALLS' || allocation.optionSelectionMode === 'ALL_PUTS') {
                        optnModes.push(allocation.optionSelectionMode);
                    }
                }
            });
        }
        return optnModes;
    }

    getRowCount() {
        return (this.data.allocations) ? this.data.allocations.length : 0;
    }

    deleteAll() {
        this.data.allocations = [];
        this.loadGrid();
        this.change.emit(true);
    }

    delete(allocation: any) {
        let allocations: ICoveredCallAllocation[] = [];
        for (let i = 0; i < this.data.allocations.length; i++) {
            if (this.data.allocations[i].underlyingSymbol !== allocation.underlyingSymbol) {
                allocations.push(this.data.allocations[i]);
            }
            else {
                if (allocation.optionSelectionMode === 'OPTION_CHAIN') {
                    if (this.data.allocations[i].optionSymbol !== allocation.optionSymbol) {
                        allocations.push(this.data.allocations[i]);
                    }
                } else {
                    if (this.data.allocations[i].optionSelectionMode !== allocation.optionSelectionMode) {
                        allocations.push(this.data.allocations[i]);
                    }
                }
            }
        }
        this.data.allocations = allocations;
        this.loadGrid();
        this.change.emit(true);
    }

    cancelDelete() {
        this.ctas.remove();
    }

    isExternalFilterPresent() {
        return true;
    }

    doesExternalFilterPass(node: any) {
        return !node.data.isUIdeleted;
    }

    @Input()
    get data() {
        return this._data;
    }

    set data(data: ICoveredCall) {
        this._data = data;
    }

    @Input()
    get isReadOnly() {
        return this._isReadOnly;
    }

    set isReadOnly(isReadOnly: boolean) {
        this._isReadOnly = isReadOnly;
    }

    get defaultPercentOfCoverCallToRoll() {
        return this._defaultPercentOfCoverCallToRoll;
    }

    set defaultPercentOfCoverCallToRoll(defaultPercentOfCoverCallToRoll: number) {
        this._defaultPercentOfCoverCallToRoll = defaultPercentOfCoverCallToRoll;
    }

    get isCashTarget() {
        return this._isCashTarget;
    }

    set isCashTarget(isCashTarget: boolean) {
        this._isCashTarget = isCashTarget;
    }

    get isShortPosition() {
        return this._isShortPosition;
    }

    set isShortPosition(isShortPosition: boolean) {
        this._isShortPosition = isShortPosition;
    }

    get positionType() {
        return this._positionType;
    }

    set positionType(positionType: string) {
        this._positionType = positionType;
    }

    get securities() {
        return this._securities;
    }

    set securities(securities: IDropdownItem[]) {
        this._securities = securities;
    }

    get underlyingSymbol() {
        return this._underlyingSymbol;
    }

    set underlyingSymbol(underlyingSymbol: ISecurityDropDownItem) {
        this._underlyingSymbol = underlyingSymbol;
    }

    set grid(g: any) {
        this._grid = g;
    }

    get grid() {
        return this._grid;
    }

    set gridOptions(gridOpns: any) {
        this._gridOptions = gridOpns;
    }

    get gridOptions() {
        return this._gridOptions;
    }

    onCreateNewSecurity() {
        const dataParam: any = {
            editModel: true
        };
        this.as.fireEvent(IrbModelsEvents.SHOW_CREATE_SECURITIES, dataParam);
    }

}
